import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useAuth } from "@/services/auth";
import { useDemo } from "@/demo/state";
import { coachApi, CoachStatus, CoachError } from "@/services/coach";

/**
 * One shared copy of /api/self/coach-status for the whole portal.
 *
 * Every page used to fetch it on mount, so moving between My Coach, Settings
 * and Products re-ran the full request (session check, seat, corpus, TikTok
 * link) and showed a loader each time. Now:
 *   - the first page to mount loads it; later pages render the cached copy
 *     instantly and revalidate in the background only when it is stale;
 *   - concurrent callers share one in-flight request;
 *   - network blips and 5xx are retried twice before an error is shown, and
 *     an error never wipes data that is already on screen;
 *   - it polls only while the coach is building, once for the whole portal;
 *   - returning to the tab revalidates if the copy is over a minute old.
 */

const STALE_MS = 30_000;
const FOCUS_STALE_MS = 60_000;
const POLL_MS = 5_000;
const POLL_MAX_MS = 4 * 60_000;
const RETRY_DELAYS = [600, 1500];

type Snapshot = {
  key: string | null;
  data: CoachStatus | null;
  loading: boolean;
  noSeat: boolean;
  error: string;
  authError: boolean;
  fetchedAt: number;
};

const EMPTY: Snapshot = { key: null, data: null, loading: true, noSeat: false, error: "", authError: false, fetchedAt: 0 };
let snap: Snapshot = EMPTY;
let inflight: Promise<void> | null = null;
let generation = 0;
const listeners = new Set<() => void>();

function set(patch: Partial<Snapshot>) {
  snap = { ...snap, ...patch };
  listeners.forEach((l) => l());
}
function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

const retryable = (e: unknown) => !(e instanceof CoachError) || e.status === 0 || e.status >= 500;
const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function fetchWithRetry(fresh: boolean): Promise<CoachStatus> {
  for (let attempt = 0; ; attempt++) {
    try {
      const status = await coachApi.status({ fresh });
      if (!status?.seat || !status?.app_link || !status.billing)
        throw new CoachError("We could not read your coach status. Please try again.", 502);
      return status;
    } catch (e) {
      if (attempt >= RETRY_DELAYS.length || !retryable(e)) throw e;
      await wait(RETRY_DELAYS[attempt]);
    }
  }
}

/** Loads (or reuses the in-flight load). `force` skips the freshness check; `fresh` also bypasses the worker's TikTok-link cache. */
function load(key: string, opts: { force?: boolean; fresh?: boolean } = {}): Promise<void> {
  if (snap.key !== key) {
    // Another user or demo role: nothing cached applies.
    generation++;
    inflight = null;
    snap = { ...EMPTY, key };
  }
  if (inflight && !opts.fresh) return inflight;
  if (!opts.force && snap.data && Date.now() - snap.fetchedAt < STALE_MS) return Promise.resolve();

  const gen = ++generation;
  // Only show the full-page loader when there is nothing to show yet.
  if (!snap.data) set({ loading: true });
  const run = (async () => {
    try {
      const data = await fetchWithRetry(!!opts.fresh);
      if (gen !== generation) return;
      set({ data, loading: false, noSeat: !data.seat.exists, error: "", authError: false, fetchedAt: Date.now() });
    } catch (e) {
      if (gen !== generation) return;
      if (e instanceof CoachError && e.status === 404) {
        set({ data: null, loading: false, noSeat: true, error: "", authError: false, fetchedAt: Date.now() });
      } else {
        // Keep whatever is already on screen; just surface the error.
        set({
          loading: false,
          noSeat: false,
          error: e instanceof Error ? e.message : "Please try again.",
          authError: e instanceof CoachError && e.status === 401,
        });
      }
    } finally {
      if (gen === generation) inflight = null;
    }
  })();
  inflight = run;
  return run;
}

/** Optimistic local patch (demo builds and instant UI after a mutation). */
export function patchCoachStatus(patch: Partial<CoachStatus>) {
  if (snap.data) set({ data: { ...snap.data, ...patch } });
}

const isBuilding = (d: CoachStatus | null) =>
  d?.seat.onboarding_status === "pending_enrichment" || d?.seat.onboarding_status === "processing";

export function useCoachStatus() {
  const { user } = useAuth();
  const demo = useDemo();
  const key = `${user?.id ?? "anon"}:${demo.role}`;
  const s = useSyncExternalStore(subscribe, () => snap);
  const current = s.key === key ? s : { ...EMPTY, key };
  const [notice, setNotice] = useState("");
  const previousStatus = useRef<string>();

  // Mount / identity change: reuse the cached copy, revalidate if stale.
  useEffect(() => {
    load(key);
  }, [key]);

  // Revalidate when the tab comes back after a while.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && snap.key === key && Date.now() - snap.fetchedAt > FOCUS_STALE_MS) load(key);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [key]);

  // Poll while the coach builds. Every page shares the one store, so a second
  // mounted page does not add a second request stream (load() dedupes).
  const building = isBuilding(current.data);
  useEffect(() => {
    if (!building) return;
    const started = Date.now();
    const timer = setInterval(() => {
      if (Date.now() - started > POLL_MAX_MS) return clearInterval(timer);
      load(key, { force: true });
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [building, key]);

  // "Ready" notice on the transition out of a build.
  const status = current.data?.seat.onboarding_status;
  useEffect(() => {
    if (["pending_enrichment", "processing"].includes(previousStatus.current ?? "") && status === "ready")
      setNotice("Your coach is ready with the updated account’s videos.");
    previousStatus.current = status;
  }, [status]);

  /** Explicit refresh (buttons, after a change). Always hits the worker, bypassing its TikTok cache. */
  const refresh = (_initial = false) => load(key, { force: true, fresh: true });

  const data = current.data;
  const seat = data?.seat;
  const failed = seat?.onboarding_status === "failed";
  const cancelled = seat?.status === "cancelled";
  const tiktokState: "connected" | "needs-reconnect" | "not-connected" =
    data?.app_link.showcase_ok ? "connected" : data?.app_link.connected ? "needs-reconnect" : "not-connected";
  return {
    data,
    seat,
    loading: current.loading && !data,
    noSeat: current.noSeat,
    error: current.error,
    authError: current.authError,
    notice,
    setNotice,
    refresh,
    building,
    failed,
    cancelled,
    tiktokState,
  };
}
